/**
 * Auth slice — client-side authentication state.
 *
 * The access token is PERSISTED in localStorage, matching the clinic and
 * physician apps.
 *
 * It was memory-only, on the reasoning that the httpOnly refresh cookie would
 * restore the session and localStorage is reachable by XSS. That reasoning only
 * holds while the cookie actually arrives — and in this deployment it does not
 * (`POST /auth/refresh` → 401 on every cold start), so the client app signed
 * users out on every relaunch while the clinic app, which persists, did not.
 *
 * A token that cannot be delivered protects nobody. The cookie is still the
 * primary path: it is tried first on boot and, when present, yields a fresh
 * token. The stored token is the fallback that keeps the session alive when it
 * is not.
 */

import { createSlice } from '@reduxjs/toolkit';

// Per-origin, and namespaced like the other apps (candle.clinic.*,
// candle.physician.*) so two Candle apps on one host cannot collide.
const STORAGE_KEY = 'candle.client.accessToken';

function loadToken() {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}
function saveToken(token) {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* storage unavailable — session falls back to in-memory */ }
}

const persisted = loadToken();

/** @type {{ accessToken: string|null, user: object|null, isAuthenticated: boolean, isRefreshing: boolean }} */
const initialState = {
  accessToken: persisted,
  user: null,              // profile is re-fetched on boot
  isAuthenticated: Boolean(persisted),
  isRefreshing: false,
  // Has the server actually ANSWERED the question "does this phone have a
  // profile?" — as opposed to us not having asked yet, or the ask having
  // failed. `user === null` conflates all three, and treating "unknown" as
  // "no profile" is what showed the signup wizard to clients who already had
  // an account.
  profileLoaded: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Called after successful login or token refresh.
     * Payload: { accessToken, user (optional) }
     */
    loginSuccess(state, action) {
      state.accessToken = action.payload.accessToken;
      saveToken(action.payload.accessToken);
      // Only a definitive answer sets this. Callers that merely have a token
      // and no profile yet must pass profileLoaded: false.
      if (action.payload.profileLoaded !== undefined) {
        state.profileLoaded = action.payload.profileLoaded;
      }
      if (action.payload.user !== undefined) {
        state.user = action.payload.user;
      }
      state.isAuthenticated = !!action.payload.accessToken;
    },

    /**
     * Update only the access token (used after refresh).
     */
    setAccessToken(state, action) {
      state.accessToken = action.payload;
      saveToken(action.payload);
      state.isAuthenticated = !!action.payload;
    },

    /**
     * Update the user profile in state (after fetch or update).
     */
    setUser(state, action) {
      state.user = action.payload;
    },

    /**
     * Set the refreshing flag (used by Axios interceptor to prevent
     * concurrent refresh calls).
     */
    setRefreshing(state, action) {
      state.isRefreshing = action.payload;
    },

    /**
     * Clear all auth state — called on logout, refresh failure,
     * or inactivity timeout.
     */
    // The server answered: this phone has no profile (a genuine pre-signup
    // guest). Distinct from "we could not ask".
    setProfileLoaded(state, action) {
      state.profileLoaded = action.payload !== false;
    },
    logout(state) {
      // Must clear storage too, or signing out would leave a token that
      // signs the user straight back in on the next launch.
      saveToken(null);
      state.accessToken = null;
      state.profileLoaded = false;
      state.user = null;
      state.isAuthenticated = false;
      state.isRefreshing = false;
    },
  },
});

export const { loginSuccess, setAccessToken, setUser, setRefreshing, logout, setProfileLoaded} = authSlice.actions;

export default authSlice.reducer;

// Selectors — use these in components instead of reading state directly.
export const selectAccessToken = (state) => state.auth.accessToken;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectIsRefreshing = (state) => state.auth.isRefreshing;
// Tri-state guard: has the server answered "does this phone have a profile?"
// Route guards must read THIS, not `!user`, or they send existing clients to
// the signup wizard while the answer is still unknown.
export const selectProfileLoaded = (state) => state.auth.profileLoaded;
